import axios from "axios";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../routes/routeKeys";
import Loader from "../../components/Loader";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import AddContractToClosedData from "./components/AddContractToClosedData";

const initialData = {
  property_id: "",
  property_name: "",
  executed_loi: null,
  executed_loi_date: null,
  purchase_agreement_draft_distributed: null,
  executed_purchase_agreeement: null,
  transaction_timeline_distributed: null,
  earnest_deposite_confirmed: null,
  due_diligence_delivery_receipt_confirmed: null,
  title: null,
  address_title_issues: null,
  cordinate_inspection: null,
  survey_received: null,
  environmental_report_received: null,
  appraisal_received: null,
  negotiate_repairs: null,
  due_diligence_expiration_date: null,
  estoppel_snda_ordered: null,
  financing_approval: null,
  building_insurance_obtained: null,
  estoppel_snda_received: null,
  scheduled_closing: null,
  comission_invoice_wiring_instruction: null,
  review_settlement_statement: null,
  closed_date: null,
  fell_out_of_contract: "",
  fell_out_of_executed_loi: "",
  earnest_deposite_confirmed_days: "",
  due_diligence_delivery_receipt_confirmed_days: "",
  title_days: "",
  address_title_issues_days: "",
  due_diligence_expiration_date_days: "",
  financing_approval_days: "",
  scheduled_closing_days: "",
};

const EditContractToClosed = () => {
  const { id } = useParams();
  const [config] = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);
  const [disable, setDisable] = useState(false);
  const [formData, setFormData] = useState(initialData);

  const handleClose = () => {
    setFormData(initialData);
    setError({});
    setDisable(false);

    if (id) {
      navigate(ROUTES.STAGE_DETAILS.replace(":id", id));
    } else {
      navigate(ROUTES.CONTACT_CLOSE);
    }
  };

  const fetchDetails = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/contract-to-close/${id}`, config)
      .then((res) => {
        const data = res?.data?.data || {};

        // Helper function to clean bad/old dates - fixes timezone offset issue
        const cleanDate = (dateValue) => {
          if (!dateValue) return null;

          // If already a Date object, adjust it
          if (dateValue instanceof Date) {
            const offset = dateValue.getTimezoneOffset();
            return new Date(dateValue.getTime() + offset * 60 * 1000);
          }

          // Parse string date (expecting MM/DD/YYYY format from API)
          // Use strict parsing with format to avoid timezone issues
          const m = moment(dateValue, ["MM/DD/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"], true);
          if (!m.isValid()) return null;

          const year = m.year();
          // If year is less than 1900 → it's invalid/old default date → make it empty
          if (year < 1900) {
            return null;
          }

          // Create date at midnight local time to avoid timezone shift
          // By using the individual components, we ensure the date stays as intended
          return new Date(m.year(), m.month(), m.date());
        };

        setFormData({
          property_id: data?.property_id ?? "",
          property_name: data?.property?.property_name ?? "",
          executed_loi: cleanDate(data?.executed_loi),
          executed_loi_date: cleanDate(data?.executed_loi_date),
          purchase_agreement_draft_distributed: cleanDate(data?.purchase_agreement_draft_distributed),
          executed_purchase_agreeement: cleanDate(data?.executed_purchase_agreeement),
          transaction_timeline_distributed: cleanDate(data?.transaction_timeline_distributed),
          earnest_deposite_confirmed: cleanDate(data?.earnest_deposite_confirmed),
          due_diligence_delivery_receipt_confirmed: cleanDate(data?.due_diligence_delivery_receipt_confirmed),
          title: cleanDate(data?.title),
          address_title_issues: cleanDate(data?.address_title_issues),
          cordinate_inspection: cleanDate(data?.cordinate_inspection),
          survey_received: cleanDate(data?.survey_received),
          environmental_report_received: cleanDate(data?.environmental_report_received),
          appraisal_received: cleanDate(data?.appraisal_received),
          negotiate_repairs: cleanDate(data?.negotiate_repairs),
          due_diligence_expiration_date: cleanDate(data?.due_diligence_expiration_date),
          estoppel_snda_ordered: cleanDate(data?.estoppel_snda_ordered),
          financing_approval: cleanDate(data?.financing_approval),
          building_insurance_obtained: cleanDate(data?.building_insurance_obtained),
          estoppel_snda_received: cleanDate(data?.estoppel_snda_received),
          scheduled_closing: cleanDate(data?.scheduled_closing),
          comission_invoice_wiring_instruction: cleanDate(data?.comission_invoice_wiring_instruction),
          review_settlement_statement: cleanDate(data?.review_settlement_statement),
          fell_out_of_executed_loi: cleanDate(data?.fell_out_of_executed_loi),
          fell_out_of_contract: cleanDate(data?.fell_out_of_contract),
          closed_date: cleanDate(data?.closed_date),

          // Days fields
          earnest_deposite_confirmed_days: data?.earnest_deposite_confirmed_days ?? "",
          due_diligence_delivery_receipt_confirmed_days: data?.due_diligence_delivery_receipt_confirmed_days ?? "",
          title_days: data?.title_days ?? "",
          address_title_issues_days: data?.address_title_issues_days ?? "",
          due_diligence_expiration_date_days: data?.due_diligence_expiration_date_days ?? "",
          financing_approval_days: data?.financing_approval_days ?? "",
          scheduled_closing_days: data?.scheduled_closing_days ?? "",
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (id) {
      fetchDetails();
    }
  }, [id]);

  // ====================== VALIDATION FOR DEPENDENT DATES ======================
  const validateDependentDates = () => {
    const newErrors = {};
    let hasError = false;

    const checks = [
      { date: "earnest_deposite_confirmed", days: "earnest_deposite_confirmed_days", label: "Earnest Deposit Confirmed" },
      { date: "due_diligence_delivery_receipt_confirmed", days: "due_diligence_delivery_receipt_confirmed_days", label: "Due Diligence Delivery Confirmed" },
      { date: "title", days: "title_days", label: "Title" },
      { date: "address_title_issues", days: "address_title_issues_days", label: "Address Title Issues" },
      { date: "due_diligence_expiration_date", days: "due_diligence_expiration_date_days", label: "Due Diligence Expiration Date" },
      { date: "financing_approval", days: "financing_approval_days", label: "Financing Approval" },
      { date: "scheduled_closing", days: "scheduled_closing_days", label: "Scheduled Closing" },
    ];

    checks.forEach(({ date, days, label }) => {
      if (formData[date] && !formData[days]) {
        newErrors[days] = `Please enter ${label} days`;
        hasError = true;
      }
    });

    if (hasError) {
      setError((prev) => ({ ...prev, ...newErrors }));
      NotificationManager.error("Please fill the 'Days' field for every date you entered.");
      return false;
    }
    return true;
  };

  // Helper to format Date to MM/DD/YYYY string for API
  const formatDateForAPI = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setDisable(true);

    // Format dates for API
    const dataToSend = { ...formData };
    const dateFields = [
      "executed_loi", "executed_loi_date", "purchase_agreement_draft_distributed", "executed_purchase_agreeement",
      "transaction_timeline_distributed", "earnest_deposite_confirmed", "due_diligence_delivery_receipt_confirmed",
      "title", "address_title_issues", "cordinate_inspection", "survey_received",
      "environmental_report_received", "appraisal_received", "negotiate_repairs",
      "due_diligence_expiration_date", "estoppel_snda_ordered", "financing_approval",
      "building_insurance_obtained", "estoppel_snda_received", "scheduled_closing",
      "fell_out_of_contract", "fell_out_of_contract", "fell_out_of_contract",
      "fell_out_of_executed_loi", "fell_out_of_executed_loi", "fell_out_of_executed_loi"
    ];
    dateFields.forEach((field) => {
      if (dataToSend[field]) {
        dataToSend[field] = formatDateForAPI(dataToSend[field]);
      }
    });

    axios
      .put(`${BASE_URL}/contract-to-close/${id}`, dataToSend, config)
      .then((res) => {
        NotificationManager.success("Contract updated successfully!");
        handleClose();
      })
      .catch((err) => {
        setDisable(false);
        setError(err.response?.data?.errors || {});
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H md:px-0 px-3">Edit Contract To Close</p>
      </div>

      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-50 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
            <Loader />
          </div>
        )}
        <AddContractToClosedData
          formData={formData}
          error={error}
          onSetFormData={setFormData}
          onSetError={setError}                         // Simplified
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          disable={disable}
        />
      </div>
    </BaseLayout>
  );
};

export default EditContractToClosed;